import { useCallback, useMemo, useRef } from 'react';

import { useWebSocket } from 'api/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import { useInvalidateJourneyActions } from 'api/journey-actions/hooks';
import { useInvalidateJourneyForms } from 'api/journey-forms/hooks';
import { useInvalidateJourneys } from 'api/journeys/hooks';

import {
  isLoadingStateEvent,
  isReasoningMessageEvent,
  isStatusEvent,
  isToolCallEvent,
  type ChatMessage,
} from './use-ai-builder-chat';
import { type LoadingLabel } from './use-loading-state';

type UseAIBuilderWebSocketOptions = {
  journeyShortId: string;
  reduceByWsEvent: (allMessages: ChatMessage[], payload: any) => ChatMessage[];
  onMessagesUpdate: (updater: (msgs: ChatMessage[]) => ChatMessage[]) => void;
  onLoadingStateEvent: (label: LoadingLabel) => void;
  onClearLoadingLabel: () => void;
  onToolStart: () => void;
  onRequestEnd: () => void;
};

export const useAIBuilderWebSocket = ({
  journeyShortId,
  reduceByWsEvent,
  onMessagesUpdate,
  onLoadingStateEvent,
  onClearLoadingLabel,
  onToolStart,
  onRequestEnd,
}: UseAIBuilderWebSocketOptions) => {
  const activeToolCallIdsRef = useRef<Set<string>>(new Set());

  const { invalidateJourneyActions } = useInvalidateJourneyActions();
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();
  const { invalidateJourneyForms } = useInvalidateJourneyForms();

  const wsPath = useMemo(() => `ai-builder/${journeyShortId}/`, [journeyShortId]);

  const handleWebSocketMessage = useCallback(
    (event: MessageEvent<string>) => {
      if (!event?.data) return;

      const envelope = JSON.parse(event.data);
      if (!envelope || envelope.type !== 'update' || !envelope.payload) return;

      const payload = envelope.payload;

      onMessagesUpdate((allMessages) => reduceByWsEvent(allMessages, payload));

      if (isLoadingStateEvent(payload)) {
        onLoadingStateEvent(payload.label);
        return;
      }

      if (isReasoningMessageEvent(payload)) {
        return;
      }

      if (isStatusEvent(payload)) {
        if (['error', 'interrupted', 'completed'].includes(payload.event)) {
          activeToolCallIdsRef.current.clear();
          onRequestEnd();
        }
        return;
      }

      if (isToolCallEvent(payload)) {
        const toolPayload = payload;

        if (toolPayload.event === 'start') {
          if (toolPayload.name === 'ask_user_questions') {
            onClearLoadingLabel();
          }
          activeToolCallIdsRef.current.add(toolPayload.callId);
          onToolStart();
        } else if (toolPayload.event === 'end') {
          activeToolCallIdsRef.current.delete(toolPayload.callId);
          if (
            [
              'create_action',
              'edit_action',
              'delete_action',
              'move_action',
              'create_trigger',
            ].includes(toolPayload?.toolType ?? '')
          ) {
            if (!toolPayload.hasError) {
              invalidateJourneyActions(journeyShortId);
              invalidateJourney(journeyShortId);

              const formTools = [
                'create_combo_send_form_action',
                'edit_combo_send_form_action_config',
                'edit_combo_send_form_action_form',
              ];
              if (formTools.includes(toolPayload?.name ?? '')) {
                invalidateJourneyActionFormsList();
              }

              if (toolPayload?.name === 'create_external_form') {
                invalidateJourneyForms(journeyShortId);
              }
            }
          }
        }
      }
    },
    [
      reduceByWsEvent,
      onMessagesUpdate,
      onLoadingStateEvent,
      onClearLoadingLabel,
      onToolStart,
      onRequestEnd,
      invalidateJourneyActions,
      invalidateJourney,
      invalidateJourneyActionFormsList,
      invalidateJourneyForms,
      journeyShortId,
    ]
  );

  useWebSocket(wsPath, handleWebSocketMessage, {
    enabled: Boolean(wsPath),
    throttleTime: 0,
  });

  const clearActiveToolCalls = useCallback(() => {
    activeToolCallIdsRef.current.clear();
  }, []);

  return {
    clearActiveToolCalls,
  };
};
